import React from 'react';
import { motion } from 'framer-motion';
import { Database, DollarSignIcon, PackageIcon, TargetIcon, TruckIcon, XCircleIcon, RotateCcwIcon, ArrowRightIcon, GaugeIcon } from 'lucide-react';
import type { DashboardInputs, DashboardMetrics, CountryCode } from '../utils/calculations';
import { formatCurrency, formatNumber } from '../utils/calculations';
interface InputCardProps {
  inputs: DashboardInputs;
  metrics: DashboardMetrics;
  onChange: (field: keyof DashboardInputs, value: number) => void;
  country: CountryCode;
}
const fields: {
  key: keyof DashboardInputs;
  label: string;
  icon: React.ElementType;
  min: number;
  max: number;
  step: number;
  isCurrency: boolean;
  suffix?: string;
}[] = [{
  key: 'price',
  label: 'Precio de Venta',
  icon: DollarSignIcon,
  min: 10000,
  max: 200000,
  step: 500,
  isCurrency: true
}, {
  key: 'productCost',
  label: 'Costo del Producto',
  icon: PackageIcon,
  min: 1000,
  max: 100000,
  step: 500,
  isCurrency: true
}, {
  key: 'fleteInicial',
  label: 'Flete Inicial (Transportadora)',
  icon: TruckIcon,
  min: 1000,
  max: 50000,
  step: 500,
  isCurrency: true
}, {
  key: 'cpaIdeal',
  label: 'CPA Meta Ideal',
  icon: TargetIcon,
  min: 1000,
  max: 50000,
  step: 500,
  isCurrency: true
}, {
  key: 'cancelledPct',
  label: '% Pedidos Cancelados',
  icon: XCircleIcon,
  min: 0,
  max: 50,
  step: 1,
  isCurrency: false,
  suffix: '%'
}, {
  key: 'returnPct',
  label: '% Devoluciones (Sobre Despachados)',
  icon: RotateCcwIcon,
  min: 0,
  max: 60,
  step: 1,
  isCurrency: false,
  suffix: '%'
}];
export function InputCard({
  inputs,
  metrics,
  onChange,
  country
}: InputCardProps) {
  const handleNumberInput = (key: keyof DashboardInputs, raw: string) => {
    const cleaned = raw.replace(/[^0-9]/g, '');
    const num = parseFloat(cleaned);
    if (!isNaN(num)) {
      onChange(key, num);
    } else if (cleaned === '') {
      onChange(key, 0);
    }
  };
  return <motion.div initial={{
    opacity: 0,
    x: -20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.5,
    delay: 0.1
  }} className="glass-card p-6 h-full flex flex-col" data-id="element-101">
      <div className="flex items-center gap-3 mb-5" data-id="element-102">
        <div className="p-2 bg-white/5 rounded-lg" data-id="element-103">
          <Database className="w-5 h-5 text-white/70" data-id="element-104" />
        </div>
        <h2 className="text-sm font-bold tracking-widest text-white/70 uppercase" data-id="element-105">
          Datos de Entrada
        </h2>
      </div>

      <div className="space-y-3 flex-grow flex flex-col justify-center" data-id="element-106">
        {fields.map(field => {
        const value = inputs[field.key];
        const pct = (value - field.min) / (field.max - field.min) * 100;
        return <div key={field.key} data-id="element-107">
              <div className="group rounded-xl p-2.5 hover:bg-white/5 transition-colors" data-id="element-108">
                <div className="flex items-center justify-between mb-1.5" data-id="element-109">
                  <div className="flex items-center gap-2" data-id="element-110">
                    <field.icon className="w-3.5 h-3.5 text-white/40 group-hover:text-salmon transition-colors flex-shrink-0" data-id="element-111" />
                    <span className="text-xs font-medium text-white/60" data-id="element-112">
                      {field.label}
                    </span>
                  </div>
                  <input type="text" inputMode="numeric" value={field.isCurrency ? formatNumber(value, country) : value} onChange={e => handleNumberInput(field.key, e.target.value)} className="w-24 text-right bg-white/[0.04] border border-white/10 rounded-lg px-2 py-1 text-sm font-bold text-white focus:outline-none focus:border-salmon/50 transition-colors" data-id="element-113" />
                </div>
                <div className="relative" data-id="element-114">
                  <div className="absolute top-1/2 left-0 h-1.5 rounded-full bg-gradient-to-r from-salmon/60 to-salmon pointer-events-none -translate-y-1/2 z-0" style={{
                width: `${pct}%`
              }} data-id="element-115" />
                  <input type="range" min={field.min} max={field.max} step={field.step} value={value} onChange={e => onChange(field.key, parseFloat(e.target.value))} className="relative z-10 w-full" data-id="element-116" />
                </div>
              </div>

              {/* Flete Real after Flete Inicial */}
              {field.key === 'fleteInicial' && <div className="mx-2.5 mt-1 mb-1 flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.02] border border-white/5" data-id="element-117">
                  <ArrowRightIcon className="w-3 h-3 text-salmon/50 flex-shrink-0" data-id="element-118" />
                  <span className="text-[10px] font-medium text-white/40" data-id="element-119">
                    Flete Real
                  </span>
                  <span className="ml-auto text-xs font-bold text-salmon/80" data-id="element-120">
                    {formatCurrency(metrics.fleteReal, country)}
                  </span>
                </div>}
            </div>;
      })}

        {/* Calculated Efficiency */}
        <div className="mx-2.5 mt-2 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-cyber-green/5 border border-cyber-green/10" data-id="element-121">
          <GaugeIcon className="w-4 h-4 text-cyber-green/60 flex-shrink-0" data-id="element-122" />
          <div className="flex flex-col" data-id="element-123">
            <span className="text-[10px] font-bold text-cyber-green/60 uppercase tracking-wider" data-id="element-124">
              Eficiencia Final
            </span>
            <span className="text-[9px] text-white/30" data-id="element-125">
              ({metrics.dispatchedPct}% despachados × {100 - metrics.returnPct}%
              entregados)
            </span>
          </div>
          <span className="ml-auto text-lg font-black text-cyber-green" data-id="element-126">
            {metrics.efficiencyFinal.toFixed(1)}%
          </span>
        </div>
      </div>
    </motion.div>;
}